import * as React from "react";

import { cn } from "../../lib/utils";

/** Small lifecycle-state dot — replaces every filled status pill and lifecycle Badge. */

export type StatusKind =
  | "backlog"
  | "todo"
  | "progress"
  | "review"
  | "done"
  | "blocked"
  | "cancelled"
  | "onhold";

export interface StatusDotProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "color" | "children"
> {
  /** Lifecycle state; drives the colour from the `--color-status-*` tokens. */
  status?: StatusKind;
  /** Explicit CSS colour override for data-driven board colours. */
  color?: string;
  /** `` reads as unstarted / planned; `solid` as an engaged state. */
  variant?: "solid" | "muted";
  /** 6px (`sm`) or 8px (`default`). */
  size?: "sm" | "default";
  /** Accessible name; also the visible text when `showLabel` is set. */
  label?: string;
  showLabel?: boolean;
}

/** Text colour per state — the dot fill derives from it. */
const statusToneClass: Record<StatusKind, string> = {
  backlog: "text-status-backlog",
  todo: "text-status-todo",
  progress: "text-status-progress",
  review: "text-status-review",
  done: "text-status-done",
  blocked: "text-status-blocked",
  cancelled: "text-status-cancelled",
  onhold: "text-status-onhold",
};

const dotSizeClass = {
  sm: "size-1.5",
  default: "size-2",
} as const;

/** States that read as "not started yet" default to the muted treatment. */
const MUTED_BY_DEFAULT: ReadonlySet<StatusKind> = new Set<StatusKind>([
  "todo",
  "backlog",
]);

const StatusDot = React.forwardRef<HTMLSpanElement, StatusDotProps>(
  (
    {
      status,
      color,
      variant,
      size = "default",
      label,
      showLabel = false,
      className,
      style,
      ...props
    },
    ref,
  ) => {
    const resolvedVariant =
      variant ?? (status && MUTED_BY_DEFAULT.has(status) ? "muted" : "solid");
    const accessibleName = label ?? status ?? "status";
    const toneClass = color ? undefined : statusToneClass[status ?? "backlog"];

    const dotClassName = cn(
      "inline-block shrink-0 rounded-full",
      dotSizeClass[size],
      toneClass,
      resolvedVariant === "solid" ? "bg-current" : "bg-current opacity-40",
    );
    const dotStyle = color ? { color, ...style } : style;

    if (!showLabel) {
      return (
        <span
          ref={ref}
          data-slot="status-dot"
          data-status={status}
          role="img"
          aria-label={accessibleName}
          className={cn(dotClassName, className)}
          style={dotStyle}
          {...props}
        />
      );
    }

    return (
      <span
        ref={ref}
        data-slot="status-dot"
        data-status={status}
        className={cn(
          "inline-flex items-center gap-1.5 text-sm text-text",
          className,
        )}
        {...props}
      >
        <span aria-hidden="true" className={dotClassName} style={dotStyle} />
        <span className="truncate">{label ?? accessibleName}</span>
      </span>
    );
  },
);
StatusDot.displayName = "StatusDot";

export { StatusDot };
